/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo, useState } from "react";
import { TViewComponent } from "../../../../view/IView";
import { ReadmeEditorView } from "../ReadmeEditorView";
import { README_EDITOR_VIEW_MODE } from "../../types";
import { useSimpleEvent } from "../../../../ui/hooks/useSimpleEvent";
import { ReadmeViewMode } from "./ReadmeViewMode";
import { ReadmeEditMode } from "./ReadmeEditMode";
import { ReadmeEditorDebug } from "./ReadmeEditorDebug";
import { ReadmeEditorToolbar } from "./ReadmeEditorToolbar";
import { ReadmeEditorSecondaryToolbar } from "./ReadmeEditorSecondaryToolbar";
import { ReadmeEditorOutline } from "./ReadmeEditorOutline";
import { ReadmeEditorOutlineToolbar } from "./ReadmeEditorOutlineToolbar";
import { ReadmeEditorInspector } from "./ReadmeEditorInspector";
import { Layout } from "../../../../ui/components/Layout/Layout";

export const ReadmeEditorMain: TViewComponent<ReadmeEditorView> = ({ view }) => {
	const layoutState = useMemo(() => view.getLayout(), [ view ]);
	const model = useMemo(() => view.getModel(), [ view ]);
	const [ editorMode, setEditorMode ] = useState(() => view.getMode());

	useSimpleEvent(view.onModeChange, () => setEditorMode(view.getMode()), [ view ], true);

	const renderModeContent = () => {
		switch (editorMode) {
			case README_EDITOR_VIEW_MODE.VIEW:
				return <ReadmeViewMode view={view} />;
			case README_EDITOR_VIEW_MODE.EDIT:
				return <ReadmeEditMode view={view} />;
			case README_EDITOR_VIEW_MODE.DEBUG:
				return <ReadmeEditorDebug view={view} />;
		}
	};

	const renderComponent = useCallback(
		(componentName: string) => {
			switch (componentName) {
				case "outlineContent":
					return <ReadmeEditorOutline readmeModel={model} />;
				case "outlineToolbar":
					return <ReadmeEditorOutlineToolbar readmeModel={model} />;
				case "mainContent":
					return (
						<div className="edt-readme-editor__content">
							{renderModeContent()}
						</div>
					);
				case "mainToolbar":
					return <ReadmeEditorToolbar view={view} />;
				case "secondaryToolbar":
					return <ReadmeEditorSecondaryToolbar view={view} />;
				case "inspector":
					return <ReadmeEditorInspector readmeModel={model} />;
				default:
					return <p>Error: Undefined panel {componentName}.</p>;
			}
		},
		[ view, model, editorMode ]
	);

	return <Layout state={layoutState} renderComponent={renderComponent} />;
};
